import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './component-css/Home.css';

const HERO_COLORS = ['#cc4444', '#2f4893', '#235223', '#bc8b11'];

function Home() {
    const HERO_LETTER_COUNT = 7;

    const [githubRepos, setGithubRepos] = useState([]);
    const [loadingRepos, setLoadingRepos] = useState(true);

    // Coding projects state
    const [isProjectsExpanded, setIsProjectsExpanded] = useState(false);
    const [isGithubExpanded, setIsGithubExpanded] = useState(false);

    const caseStudies = [
        {
            id: 'trident',
            title: 'Project Trident',
            role: 'Built at Urban Strategy · Apr 2026 – present',
            description: 'Project management and billing for AEC firms. I rebuilt the calendar, scheduling, navigation and most of the mobile app: 193 commits and counting.',
            image: '/assets/trident/trident-lockup.png',
            caseStudy: '/work/trident',
            isLogo: true,
            logoClass: 'logo-trident'
        },
        {
            id: 'rhomedog',
            title: 'rhomeDOG',
            role: 'Founder',
            description: 'A Dallas events and culture platform I designed and built solo: RSVPs and waitlists, member profiles, a Stripe vendor marketplace and a journal.',
            image: '/assets/pictures/rhomedog-landing.png',
            caseStudy: '/work/rhomedog'
        },
        {
            id: 'urban-strategy',
            title: 'Urban Strategy',
            role: 'Technical Software Engineer · Apr 2026 – present',
            description: "I build Project Trident and the firm's internal software, integrations and automation, and support its IT systems.",
            previous: 'Previously Survey Field Technician (Feb – Apr 2026): led field crews on boundary, topo, ALTA and construction staking surveys with robotic total stations and RTK GNSS, then processed the data in Trimble Business Center and drafted in Civil 3D.',
            image: '/assets/pictures/urban_logo.png',
            linkUrl: 'https://urbanstrategy.us/',
            isLogo: true,
            logoClass: 'logo-urban'
        }
    ];

    const codingProjects = [
        {
            id: 6,
            title: 'Project Trident',
            description: 'A construction/engineering/architecture project management webapp.',
            image: '/assets/trident/trident-landing.png',
            linkUrl: 'https://projecttrident.us/home'
        },
        {
            id: 5,
            title: 'rhomedog',
            description: 'A Dallas events and culture platform.',
            image: '/assets/pictures/rhomedog-landing.png',
            linkUrl: 'https://www.rhomedog.com/'
        },
        {
            id: 4,
            title: 'Thalassaxir',
            description: 'An Elixir programming language experimental visualizer.',
            image: '/assets/pictures/thalassaxir.png',
            linkUrl: 'https://thalassaxir.com/'
        },
        {
            id: 3,
            title: 'Green Room',
            description: 'A local music and venue gigging platform.',
            image: '/assets/greenroom.png',
            linkUrl: 'https://www.thegrnrm.com/'
        },
        {
            id: 2,
            title: 'Wild Forest LLC',
            description: 'Website for a forestry and land management company.',
            image: '/assets/pictures/wildforest.png',
            linkUrl: 'https://www.wildforestllc.com/'
        },
        {
            id: 1,
            title: 'emrg-docs',
            description: 'A modern documentation platform with elegant design and seamless user experience.',
            image: 'https://i.imgur.com/vsWWRes.jpeg',
            linkUrl: 'https://dev.joinemrg.com/'
        }
    ];

    useEffect(() => {
        fetch('https://api.github.com/users/miloetz/repos?sort=updated&per_page=6')
            .then(res => res.json())
            .then(data => {
                const repos = Array.isArray(data) ? data : [];
                setGithubRepos(repos.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));
                setLoadingRepos(false);
            })
            .catch(() => setLoadingRepos(false));
    }, []);

    const skills = [
        'JavaScript', 'TypeScript', 'React', 'Python', 'C++',
        'HTML/CSS', 'Figma', 'Git', 'SQL', 'Node.js'
    ];

    const createDefaultLetterStyles = () => (
        Array(HERO_LETTER_COUNT).fill({ color: '', rotation: 0, scale: 1 })
    );

    const [letterStyles, setLetterStyles] = useState(createDefaultLetterStyles());

    const handleLetterHover = (index) => {
        const randomColor = HERO_COLORS[Math.floor(Math.random() * HERO_COLORS.length)];
        const randomRotation = Math.floor(Math.random() * 31) - 15; // Random rotation between -15 and +15 degrees
        setLetterStyles(prev => {
            const newStyles = [...prev];
            newStyles[index] = { color: randomColor, rotation: randomRotation, scale: 1.6 };
            return newStyles;
        });

        setTimeout(() => {
            setLetterStyles(prev => {
                const resetStyles = [...prev];
                resetStyles[index] = { color: '', rotation: 0, scale: 1 };
                return resetStyles;
            });
        }, 1500);
    };

    return (
        <div className="home">
            <section className="hero">
                <div className="hero-content">
                    <h1>
                        <span
                            className="letter"
                            style={{ 
                                color: letterStyles[0].color,
                                transform: letterStyles[0].rotation !== 0 ? `scale(${letterStyles[0].scale}) rotate(${letterStyles[0].rotation}deg)` : undefined
                            }}
                            onMouseEnter={() => handleLetterHover(0)}
                        >
                            m
                        </span>
                        <span
                            className="letter"
                            style={{ 
                                color: letterStyles[1].color,
                                transform: letterStyles[1].rotation !== 0 ? `scale(${letterStyles[1].scale}) rotate(${letterStyles[1].rotation}deg)` : undefined
                            }}
                            onMouseEnter={() => handleLetterHover(1)}
                        >
                            i
                        </span>
                        <span
                            className="letter"
                            style={{ 
                                color: letterStyles[2].color,
                                transform: letterStyles[2].rotation !== 0 ? `scale(${letterStyles[2].scale}) rotate(${letterStyles[2].rotation}deg)` : undefined
                            }}
                            onMouseEnter={() => handleLetterHover(2)}
                        >
                            l
                        </span>
                        <span
                            className="letter"
                            style={{ 
                                color: letterStyles[3].color,
                                transform: letterStyles[3].rotation !== 0 ? `scale(${letterStyles[3].scale}) rotate(${letterStyles[3].rotation}deg)` : undefined
                            }}
                            onMouseEnter={() => handleLetterHover(3)}
                        >
                            o
                        </span>{' '}
                        <span
                            className="letter"
                            style={{ 
                                color: letterStyles[4].color,
                                transform: letterStyles[4].rotation !== 0 ? `scale(${letterStyles[4].scale}) rotate(${letterStyles[4].rotation}deg)` : undefined
                            }}
                            onMouseEnter={() => handleLetterHover(4)}
                        >
                            e
                        </span>
                        <span
                            className="letter"
                            style={{ 
                                color: letterStyles[5].color,
                                transform: letterStyles[5].rotation !== 0 ? `scale(${letterStyles[5].scale}) rotate(${letterStyles[5].rotation}deg)` : undefined
                            }}
                            onMouseEnter={() => handleLetterHover(5)}
                        >
                            t
                        </span>
                        <span
                            className="letter"
                            style={{ 
                                color: letterStyles[6].color,
                                transform: letterStyles[6].rotation !== 0 ? `scale(${letterStyles[6].scale}) rotate(${letterStyles[6].rotation}deg)` : undefined
                            }}
                            onMouseEnter={() => handleLetterHover(6)}
                        >
                            z
                        </span>
                    </h1>
                    <p>Designer & developer based in the United States.</p>
                </div>
            </section>

            <section className="section">
                <h2>About.</h2>
                <div className="section-content">
                    <p>
                        I'm a developer and designer based in the United States. I studied computer science with a minor in design, making things that are both functional and beautiful.
                    </p>
                    <p>
                        When I'm not building, I'm probably running or making something.
                    </p>
                </div>
            </section>

            <section className="section">
                <h2>Stack.</h2>
                <div className="section-content">
                    <div className="skills-grid">
                        {skills.map((skill, i) => (
                            <span key={i} className="skill-tag">{skill}</span>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section">
                <h2>Featured Work.</h2>
                <div className="section-content">
                    <div className="case-studies-grid">
                        {caseStudies.map(project => (
                            <div key={project.id} className="case-study-card">
                                <div className={`case-study-media ${project.isLogo ? 'case-study-media-logo' : ''}`}>
                                    {project.image ? (
                                        <img src={project.image} alt={project.title} className={project.logoClass || ''} />
                                    ) : (
                                        <span className="case-study-initial">{project.title.charAt(0)}</span>
                                    )}
                                </div>
                                <h3>
                                    {project.caseStudy ? (
                                        <Link to={project.caseStudy} className="project-link">
                                            {project.title}
                                        </Link>
                                    ) : project.linkUrl ? (
                                        <a href={project.linkUrl} target="_blank" rel="noopener noreferrer" className="project-link">
                                            {project.title}
                                            <span className="external-arrow"> ↗</span>
                                        </a>
                                    ) : project.title}
                                </h3>
                                <p className="case-study-role">{project.role}</p>
                                <p className="case-study-description">{project.description}</p>
                                {project.previous && (
                                    <p className="case-study-previous">{project.previous}</p>
                                )}
                                {project.caseStudy && (
                                    <Link to={project.caseStudy} className="case-study-cta">see the work →</Link>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section section-coding" id="coding-work">
                <h2>WORK.</h2>
                <div className={`coding-projects-container ${isProjectsExpanded ? 'expanded' : ''}`}>
                    <div className="section-content">
                        {codingProjects.map(project => (
                            <div key={project.id} className="coding-project-item">
                                <div className="project-media-preview">
                                    <img
                                        src={project.image}
                                        alt={project.title}
                                        className="project-thumbnail"
                                    />
                                </div>
                                <div className="project-info-condensed">
                                    <h3>
                                        <a
                                            href={project.linkUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="project-link"
                                        >
                                            {project.title}
                                            <span className="external-arrow"> ↗</span>
                                        </a>
                                    </h3>
                                    <p>{project.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    {!isProjectsExpanded && <div className="fade-overlay"></div>}
                </div>
                <button
                    className={`show-more-btn ${isProjectsExpanded ? 'expanded' : ''}`}
                    aria-expanded={isProjectsExpanded}
                    onClick={() => setIsProjectsExpanded(prev => !prev)}
                >
                    {isProjectsExpanded ? 'show less' : 'show more'}
                </button>
            </section>

            <section className="section section-github">
                <h2>GitHub.</h2>
                <div className={`github-container ${isGithubExpanded ? 'expanded' : ''}`}>
                    <div className="section-content">
                        {loadingRepos ? (
                            <p className="loading-text">Loading repos...</p>
                        ) : (
                            <div className="github-grid">
                                {githubRepos.map(repo => (
                                    <a
                                        key={repo.id}
                                        href={repo.html_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="github-card"
                                    >
                                        <div className="github-card-header">
                                            <span className="github-repo-name">{repo.name}</span>
                                            {repo.language && (
                                                <span className="github-language" data-lang={repo.language.toLowerCase()}>
                                                    {repo.language}
                                                </span>
                                            )}
                                        </div>
                                        <p className="github-description">
                                            {repo.description || 'No description'}
                                        </p>
                                        <div className="github-stats">
                                            <span className="github-stat">
                                                <svg viewBox="0 0 16 16" width="14" height="14">
                                                    <path fill="currentColor" d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.75.75 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25z"/>
                                                </svg>
                                                {repo.stargazers_count}
                                            </span>
                                            <span className="github-stat">
                                                <svg viewBox="0 0 16 16" width="14" height="14">
                                                    <path fill="currentColor" d="M5 5.372v.878c0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75v-.878a2.25 2.25 0 1 1 1.5 0v.878a2.25 2.25 0 0 1-2.25 2.25h-1.5v2.128a2.251 2.251 0 1 1-1.5 0V8.5h-1.5A2.25 2.25 0 0 1 3.5 6.25v-.878a2.25 2.25 0 1 1 1.5 0ZM5 3.25a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Zm6.75.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm-3 8.75a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z"/>
                                                </svg>
                                                {repo.forks_count}
                                            </span>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                    {!isGithubExpanded && <div className="fade-overlay github-fade"></div>}
                </div>
                <button
                    className={`show-more-btn github-show-more ${isGithubExpanded ? 'expanded' : ''}`}
                    aria-expanded={isGithubExpanded}
                    onClick={() => setIsGithubExpanded(prev => !prev)}
                >
                    {isGithubExpanded ? 'show less' : 'show more'}
                </button>
            </section>

        </div>
    );
}

export default Home;
